'use client';

import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  Grid,
  Chip,
  Stepper,
  Step,
  StepLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Groups,
  EventRepeat,
  ListAlt,
  HowToReg,
  Mic,
  AutoAwesome,
  Insights,
  TaskAlt,
  CalendarMonth,
  Description,
  PhoneAndroid,
  FolderShared,
  CheckCircle,
  WarningAmber,
  ArrowForward,
  ExpandMore,
} from '@mui/icons-material';
import Header from '../../components/HomepageContent/Header';
import FeatureHeroCtaStack from '../components/FeatureHeroCtaStack';
import FeatureResourceButtons from '../components/FeatureResourceButtons';
import Footer from '../../components/HomepageContent/Footer';
import Link from 'next/link';
import BrandName from '../../components/BrandName';
import WorkflowStepper from '../../components/WorkflowStepper';
import '../features-responsive.css';

const BRAND_BLUE = '#0F4C9C';
const BRAND_GREEN = '#63A821';

const workflowSteps = [
  { title: 'Set Up Committee', description: 'Define purpose, members, roles, and meeting frequency' },
  { title: 'Schedule', description: 'Auto-generate recurring meetings or create ad-hoc/special meetings' },
  { title: 'Run Meeting', description: 'Work the agenda live with the guided Note Taker' },
  { title: 'Capture & Decide', description: 'Record minutes, attendance, QAPI data, and decisions' },
  { title: 'Conclude & Report', description: 'Generate summaries/PDFs and link action plans for follow-up' }
];

const painPoints = [
  'Committee minutes scattered across Word docs, email, and personal drives',
  'QAPI committee meetings that are hard to prove happened on cadence during survey',
  'Manual agenda building and re-typing the same recurring items every month',
  'No clear link between meeting decisions and the action plans that follow',
  'Attendance, quorum, and follow-through tracked on paper sign-in sheets',
  'Hours lost compiling meeting minutes and QAPI evidence for leadership and surveyors',
];

const capabilities = [
  {
    icon: Groups,
    title: 'Committee Setup & Membership',
    description: 'Create standing or ad-hoc committees with a defined purpose, type, and category. Assign members with roles—Chair, Secretary, Facilitator, Recorder—set a default meeting location, and manage everything per facility.',
  },
  {
    icon: EventRepeat,
    title: 'Modernized Recurring Scheduling',
    description: 'Configure meeting frequency once—weekly, monthly, quarterly, or by day/week of month—and let CareAscendium auto-generate the next meeting. Ad-hoc and special meetings are supported when something can\'t wait.',
  },
  {
    icon: ListAlt,
    title: 'Reusable Agenda Templates',
    description: 'Build standardized agenda templates with sections, items, and sub-items so every meeting starts consistent. New meetings pre-populate the agenda seamlessly—no retyping recurring standing items.',
  },
  {
    icon: HowToReg,
    title: 'Digital Attendance Tracking',
    description: 'Record present and absent members with names captured directly from your committee roster. Maintain a clean, exportable attendance record for quorum and compliance—no paper sign-in sheets.',
  },
  {
    icon: Mic,
    title: 'Guided Note Taker & Minutes',
    description: 'Walk agenda item by agenda item with the in-meeting Note Taker. Capture discussion, decisions, and minutes—with voice-to-text input—then sync everything into a clean, structured minutes record.',
  },
  {
    icon: AutoAwesome,
    title: 'AI Meeting Summaries',
    description: 'Generate a concise AI summary from the meeting\'s agenda, notes, and decisions. Review and edit, then save it to the meeting record or email it to attendees and stakeholders in a click.',
  },
  {
    icon: Insights,
    title: 'QAPI Data Capture & Trends',
    description: 'Enter QAPI metric data inside the meeting, link QAPI forms, and view data trends over time. Connect quality measures and F-tag evidence directly to the committee that reviews them.',
  },
  {
    icon: TaskAlt,
    title: 'Action Plans & PIP / PDSA Links',
    description: 'Turn decisions into accountability. Create action items, link Performance Improvement Plans and PDSA cycles, assign owners and due dates, and review progress at the next meeting.',
  },
  {
    icon: CalendarMonth,
    title: 'Calendar Invites & Notifications',
    description: 'Send calendar (.ics) invites to members and external recipients, and keep everyone aligned with instant email, SMS, and push notifications plus targeted nudges for follow-up items.',
  },
  {
    icon: Description,
    title: 'Survey-Ready PDF Reports',
    description: 'Generate professional meeting minutes and committee reports as PDFs—attendance roster, full agenda, discussion notes, decisions, action items, and next meeting—configurable by section.',
  },
  {
    icon: FolderShared,
    title: 'Documents & Meeting History',
    description: 'Attach presentations, reports, and supporting files to any meeting, and keep a complete searchable meeting-minutes history with a communication log—ready when leadership or surveyors ask.',
  },
  {
    icon: PhoneAndroid,
    title: 'Mobile Access',
    description: 'View, create, and edit meetings from the CareAscendium mobile app. Browse upcoming and past meetings, review attendance, minutes, action plans, and documents across six dedicated tabs.',
  },
];

const roles = [
  {
    title: 'Committee chairs & facilitators',
    benefit: 'Pre-built agendas, in-meeting note taking, one-click minutes and summaries',
  },
  {
    title: 'QAPI & quality leaders',
    benefit: 'Meeting evidence on cadence with QAPI data, trends, and action plans in one place',
  },
  {
    title: 'Administrators & DONs',
    benefit: 'Visibility into committee activity, attendance, and follow-through across the facility',
  },
  {
    title: 'Recorders & secretaries',
    benefit: 'Guided minutes capture, voice-to-text, and professional PDF generation in minutes',
  },
  {
    title: 'Corporate / multi-site operators',
    benefit: 'Consistent committee structure and reporting across every facility',
  },
];

const outcomes = [
  { value: 'Mins', label: 'Minutes prep time', detail: 'Minutes from the meeting to a finished record, not hours' },
  { value: '100%', label: 'On-cadence evidence', detail: 'Recurring meetings auto-scheduled so cadence is provable' },
  { value: 'One', label: 'Single source of truth', detail: 'Agenda, minutes, QAPI data, and actions in one record' },
  { value: 'High', label: 'Action follow-through', detail: 'Decisions linked to action plans and reviewed next meeting' },
  { value: 'Easy', label: 'Attendance & quorum', detail: 'Digital roster tracking instead of paper sign-in sheets' },
  { value: 'Fast', label: 'Reporting speed', detail: 'One-click PDF minutes vs. manual compilation' },
];

const integrations = [
  'QAPI Data',
  'Action Plans',
  'PIP / PDSA',
  'Grievance Management',
  'Incident Investigation',
  'Audit Findings',
  'Form Wizard',
  'Document Vault',
  'Due Items',
];

const faqs = [
  {
    q: 'Can recurring meetings be scheduled seamlessly?',
    a: 'Yes. Set a committee\'s meeting frequency once—weekly, monthly, quarterly, or by day/week of month—and CareAscendium generates the next meeting. Ad-hoc and special meetings are also supported.',
  },
  {
    q: 'How are meeting minutes created?',
    a: 'The guided Note Taker walks each agenda item so you capture discussion and decisions live, including voice-to-text input. You can generate an AI summary and a professional PDF of the full minutes.',
  },
  {
    q: 'Does it track QAPI data?',
    a: 'Yes. QAPI metric data can be entered inside the meeting, QAPI forms can be linked, and data trends are viewable over time—connected to the committee that reviews them.',
  },
  {
    q: 'Does it work on mobile?',
    a: 'Yes. CareAscendium includes meeting browsing, creation, and editing on iOS and Android, with dedicated tabs for details, attendance, minutes, action plans, documents, and the next meeting.',
  },
  {
    q: 'Can we invite people outside the system to a meeting?',
    a: 'Yes. Calendar (.ics) invites can be sent to committee members and external recipients, alongside email, SMS, and push notifications.',
  },
  {
    q: 'Is this only for QAPI committees?',
    a: 'No. Any standing or ad-hoc committee can be managed—QAPI, infection control, safety, ethics, and more—each with its own agenda templates, members, and cadence.',
  },
];

export default function CommitteeMeetingManagementFeaturePage() {
  const [expanded, setExpanded] = useState<number | false>(0);

  const handleChange = (panel: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  useEffect(() => {
    document.title = 'Committee & Meeting Management Software for Skilled Nursing Facilities | CareAscendium';
  }, []);

  return (
    <Box className="feature-page-wrapper" sx={{ minHeight: '100vh', bgcolor: '#fafafa', display: 'flex', flexDirection: 'column' }}>
      <Header />

      {/* Hero */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${BRAND_BLUE} 0%, #1E4A8C 50%, #2A5A9C 100%)`,
          py: { xs: 8, md: 12 },
          px: 4,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <Chip
            label="Committee & Meeting Management"
            sx={{
              bgcolor: 'rgba(255,255,255,0.15)',
              color: 'white',
              fontWeight: 600,
              mb: 3,
              border: '1px solid rgba(255,255,255,0.3)',
            }}
          />
          <Typography
            variant="h1"
            sx={{
              color: 'white',
              mb: 3,
              fontWeight: 800,
              fontSize: { xs: '2rem', md: '3.25rem' },
              lineHeight: 1.15,
              maxWidth: 940,
              mx: 'auto',
            }}
          >
            Run Every Committee Meeting on Cadence—With Minutes, QAPI Data, and Action Plans in One Place
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: 'rgba(255,255,255,0.92)',
              maxWidth: 840,
              mx: 'auto',
              lineHeight: 1.6,
              fontWeight: 300,
              fontSize: { xs: '1.05rem', md: '1.35rem' },
              mb: 5,
            }}
          >
            <BrandName variant="dark" style={{ fontSize: 'inherit' }} /> gives skilled nursing facilities a complete committee and meeting lifecycle—from recurring scheduling and agenda templates through attendance, AI-assisted minutes, QAPI data capture, and survey-ready reports—on web and mobile.
          </Typography>
          <FeatureHeroCtaStack>
            <Link href="/contact" style={{ textDecoration: 'none' }}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
                sx={{
                  bgcolor: BRAND_GREEN,
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  fontWeight: 700,
                  borderRadius: '6px',
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#55901d' },
                }}
              >
                REQUEST A DEMO
              </Button>
            </Link>
            <FeatureResourceButtons />
          </FeatureHeroCtaStack>
        </Container>
      </Box>

      {/* Problem */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Grid container spacing={5} alignItems="center" justifyContent="center">
          <Grid size={{ xs: 12, md: 10 }} sx={{ textAlign: 'center' }}>
            <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 3, fontWeight: 700, fontSize: { xs: '1.75rem', md: '2.25rem' } }}>
              The Challenge with Word Docs and Sign-In Sheets
            </Typography>
            <Typography sx={{ color: '#666', mb: 4, lineHeight: 1.7, fontSize: '1.05rem', maxWidth: 850, mx: 'auto' }}>
              In most SNFs, QAPI and other committee meetings are managed with recycled agendas, hand-written sign-in sheets, and minutes saved to someone's laptop. Proving that the right people met on cadence—and acted on what they decided—becomes a scramble when surveyors arrive.
            </Typography>
            <Box component="ul" sx={{ pl: 0, listStyle: 'none', m: 0, display: 'inline-block', textAlign: 'left' }}>
              {painPoints.map((point) => (
                <Box
                  component="li"
                  key={point}
                  sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5, gap: 1.5 }}
                >
                  <WarningAmber sx={{ color: '#FF9800', mt: 0.2, flexShrink: 0 }} />
                  <Typography sx={{ color: '#555', lineHeight: 1.5 }}>{point}</Typography>
                </Box>
              ))}
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 10 }}>
            <Paper
              elevation={4}
              sx={{
                p: 4,
                borderRadius: 3,
                background: 'linear-gradient(145deg, #F3F1FF 0%, #ffffff 100%)',
                borderLeft: `4px solid ${BRAND_BLUE}`,
              }}
            >
              <Typography
                sx={{
                  fontStyle: 'italic',
                  color: BRAND_BLUE,
                  fontSize: '1.15rem',
                  lineHeight: 1.6,
                  fontWeight: 500,
                }}
              >
                &ldquo;A committee meeting that isn't documented—on cadence, with follow-up—is a meeting that didn't happen as far as the survey is concerned.&rdquo;
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Solution / Workflow */}
      <Box sx={{ bgcolor: '#f0f4fa', py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
              One Workflow. From Agenda to Accountability.
            </Typography>
            <Typography sx={{ color: '#666', maxWidth: 780, mx: 'auto', lineHeight: 1.7, fontSize: '1.05rem' }}>
              <BrandName variant="light" style={{ fontSize: 'inherit' }} /> Committee & Meeting Management connects committee setup, recurring scheduling, the live meeting, decisions and QAPI data, and follow-up action plans in a single auditable record—replacing disconnected documents with one source of truth.
            </Typography>
          </Box>
          <WorkflowStepper steps={workflowSteps} />
        </Container>
      </Box>

      {/* Capabilities */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
            Key Capabilities
          </Typography>
        </Box>
        <Box 
          sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' }, 
            gap: 3,
            alignItems: 'stretch'
          }}
        >
          {capabilities.map((cap) => (
            <Paper
              key={cap.title}
              elevation={3}
              sx={{
                p: 3,
                height: '100%',
                borderRadius: 3,
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 28px rgba(21,60,127,0.12)',
                },
              }}
            >
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: '50%',
                  bgcolor: `${BRAND_BLUE}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                }}
              >
                <cap.icon sx={{ color: BRAND_BLUE, fontSize: 26 }} />
              </Box>
              <Typography variant="h6" sx={{ color: BRAND_BLUE, fontWeight: 700, mb: 1.5 }}>
                {cap.title}
              </Typography>
              <Typography sx={{ color: '#666', mb: 2, lineHeight: 1.6, fontSize: '0.95rem' }}>
                {cap.description}
              </Typography>
            </Paper>
          ))}
        </Box>
      </Container>

      {/* Roles + Integration */}
      <Box sx={{ bgcolor: '#f0f4fa', py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={5} justifyContent="center" alignItems="stretch">
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 3, fontWeight: 700, fontSize: '1.75rem' }}>
                Built for Every Role
              </Typography>
              {roles.map((role) => (
                <Box key={role.title} sx={{ display: 'flex', gap: 1.5, mb: 2.5 }}>
                  <CheckCircle sx={{ color: BRAND_GREEN, mt: 0.2, flexShrink: 0 }} />
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: BRAND_BLUE }}>{role.title}</Typography>
                    <Typography sx={{ color: '#666', fontSize: '0.95rem', lineHeight: 1.5 }}>
                      {role.benefit}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper
                elevation={3}
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: 3,
                  height: '100%',
                  width: '100%',
                  background: `linear-gradient(145deg, ${BRAND_BLUE} 0%, #1E4A8C 100%)`,
                  color: 'white',
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, fontSize: '1.5rem' }}>
                  Part of the <BrandName variant="dark" style={{ fontSize: 'inherit' }} /> Platform
                </Typography>
                <Typography sx={{ color: '#FFFFFF', fontWeight: 500, mb: 3, lineHeight: 1.6 }}>
                  Committee & Meeting Management is not a standalone add-on. The committee table is where your facility's quality work comes together—so meetings connect natively to:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3, justifyContent: 'center' }}>
                  {integrations.map((item) => (
                    <Chip
                      key={item}
                      label={item}
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.15)',
                        color: 'white',
                        fontWeight: 600,
                        border: '1px solid rgba(255,255,255,0.25)',
                      }}
                    />
                  ))}
                </Box>
                <Typography sx={{ fontWeight: 600, color: '#a8d86a', fontStyle: 'italic' }}>
                  One platform. One source of truth. Decisions that turn into measurable action.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Outcomes & Compliance */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
            Outcomes You Can Measure
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: 3,
            mb: 8,
            alignItems: 'stretch',
            maxWidth: 1000,
            mx: 'auto'
          }}
        >
          {outcomes.map((item) => (
            <Paper
              key={item.label}
              elevation={2}
              sx={{
                p: 3,
                textAlign: 'center',
                borderRadius: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: BRAND_GREEN, mb: 0.5 }}>
                {item.value}
              </Typography>
              <Typography sx={{ fontWeight: 700, color: BRAND_BLUE, mb: 0.5, fontSize: '0.95rem' }}>
                {item.label}
              </Typography>
              <Typography sx={{ color: '#888', fontSize: '0.85rem' }}>{item.detail}</Typography>
            </Paper>
          ))}
        </Box>

        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
            Documentation You Can Stand Behind
          </Typography>
          <Typography sx={{ color: '#666', maxWidth: 780, mx: 'auto', lineHeight: 1.7, fontSize: '1.05rem', mb: 4 }}>
            <BrandName variant="light" style={{ fontSize: 'inherit' }} /> supports the operational rigor SNFs need around committee and QAPI meeting procedures:
          </Typography>
          <Box sx={{ textAlign: 'left', width: 'fit-content', mx: 'auto' }}>
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
              <CheckCircle sx={{ color: BRAND_GREEN, mt: 0.2 }} />
              <Typography sx={{ color: '#555' }}>Recurring scheduling so meeting cadence is consistent and provable</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
              <CheckCircle sx={{ color: BRAND_GREEN, mt: 0.2 }} />
              <Typography sx={{ color: '#555' }}>Complete attendance records for quorum and participation</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
              <CheckCircle sx={{ color: BRAND_GREEN, mt: 0.2 }} />
              <Typography sx={{ color: '#555' }}>Structured minutes capturing discussion, decisions, and action items</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
              <CheckCircle sx={{ color: BRAND_GREEN, mt: 0.2 }} />
              <Typography sx={{ color: '#555' }}>QAPI metric data and trends recorded against the reviewing committee</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
              <CheckCircle sx={{ color: BRAND_GREEN, mt: 0.2 }} />
              <Typography sx={{ color: '#555' }}>Action plans and PIP/PDSA cycles linked to meeting decisions</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
              <CheckCircle sx={{ color: BRAND_GREEN, mt: 0.2 }} />
              <Typography sx={{ color: '#555' }}>Configurable PDF reports and a full meeting-minutes history for surveyor requests</Typography>
            </Box>
          </Box>
        </Box>
      </Container>

      {/* FAQ */}
      <Box sx={{ py: { xs: 10, md: 14 }, px: 4, background: 'linear-gradient(113.99deg, #153C7F 12.55%, #000820 87.43%)' }}>
        <Container maxWidth="md">
          <Typography variant="h3" sx={{ fontWeight: 800, color: 'white', mb: 6, textAlign: 'center', fontSize: { xs: '1.75rem', md: '2.5rem' } }}>
            Frequently Asked Questions
          </Typography>
          <Box>
            {faqs.map((faq, i) => (
              <Accordion 
                key={i}
                expanded={expanded === i} 
                onChange={handleChange(i)}
                sx={{ 
                  mb: 2.5, 
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '12px !important',
                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.08)'
                  },
                  '&:before': { display: 'none' }
                }}
              >
                <AccordionSummary expandIcon={<ExpandMore sx={{ color: 'white' }} />} sx={{ px: { xs: 2, md: 4 }, py: 1.5 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', letterSpacing: '0.02em' }}>{faq.q}</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: { xs: 2, md: 4 }, pb: 4 }}>
                  <Typography sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.8, fontSize: '1.05rem' }}>
                    {faq.a}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA */}
      <Box sx={{ py: 6, bgcolor: '#f0f4fa' }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              backgroundImage: `url('/assets/osha/bgdemo.svg'), linear-gradient(113.99deg, #0F4C9C 12.55%, #000820 87.43%)`,
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              borderRadius: '16px',
              minHeight: '300px',
              py: 5,
              px: 3,
              textAlign: 'center',
              color: 'white',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 15px 40px rgba(0, 8, 32, 0.12)',
            }}
          >
            <Typography variant="h2" sx={{ mb: 2, fontWeight: 800, fontSize: { xs: '1.75rem', md: '2.5rem' }, textTransform: 'uppercase' }}>
              Ready to Modernize Your Committee Meetings?
            </Typography>
            <Typography sx={{ mb: 4, opacity: 0.9, lineHeight: 1.6, fontSize: '1.1rem', maxWidth: 850 }}>
              See how <BrandName variant="dark" style={{ fontSize: 'inherit' }} /> helps SNFs run committees on cadence, document every decision, and turn meetings into measurable quality improvement—not another binder.
            </Typography>
            <Link href="/contact" style={{ textDecoration: 'none' }}>
              <Button
                variant="contained"
                size="large"
                sx={{
                  bgcolor: BRAND_GREEN,
                  color: 'white',
                  px: 5,
                  py: 1.5,
                  fontWeight: 700,
                  borderRadius: '30px',
                  textTransform: 'none',
                  fontSize: '1.1rem',
                  '&:hover': { bgcolor: '#528a1b' },
                  boxShadow: '0 4px 10px rgba(99, 168, 33, 0.2)',
                }}
              >
                REQUEST A DEMO
              </Button>
            </Link>
          </Box>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}
